import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, ArrowLeft, Trash2, Star, Mail, Phone, Briefcase, Calendar, Send, StickyNote, Pencil, X, Loader, RefreshCw, UserCheck, FileCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { recrutementsApi } from '../../utils/api';
import PiecesJointes from '../../components/PiecesJointes';

// Documents à demander/charger pour le dossier d'embauche
const DOCS_EMBAUCHE = ['Diplôme', "Attestation d'honorabilité", 'Copie CNI', 'Attestation carte vitale', 'RIB'];

const STATUTS = [
  { key: 'a_traiter', label: 'À traiter', badge: 'bg-amber-100 text-amber-800' },
  { key: 'entretien', label: 'Entretien', badge: 'bg-blue-100 text-blue-700' },
  { key: 'retenu', label: 'Retenu', badge: 'bg-emerald-100 text-emerald-700' },
  { key: 'vivier', label: 'Vivier', badge: 'bg-slate-100 text-slate-600' },
  { key: 'refuse', label: 'Refusé', badge: 'bg-rose-100 text-rose-700' },
];
const statutMeta = (k) => STATUTS.find(s => s.key === k) || STATUTS[0];

const TYPES = [
  { key: 'poste', label: 'Poste', badge: 'bg-navy-100 text-navy-700' },
  { key: 'stage', label: 'Stage', badge: 'bg-violet-100 text-violet-700' },
  { key: 'alternance', label: 'Alternance', badge: 'bg-teal-100 text-teal-700' },
];
const typeMeta = (k) => TYPES.find(t => t.key === k) || TYPES[0];
const SOURCES = ['Candidature spontanée', 'Indeed', 'Pôle emploi / France Travail', 'LinkedIn', 'Le Bon Coin', 'Cooptation', 'Autre'];

const fmtDate = (d) => { if (!d) return ''; const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d); return m ? `${m[3]}/${m[2]}/${m[1]}` : d; };
const fmtDateHeure = (d) => {
  if (!d) return '';
  const dt = new Date(d.includes('T') || d.includes(' ') ? d.replace(' ', 'T') + (d.includes('Z') ? '' : 'Z') : d);
  if (isNaN(dt)) return d;
  return dt.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const nomComplet = (r) => `${r.prenom || ''} ${r.nom || ''}`.trim() || 'Candidat';

// Étoiles « coup de cœur » (note globale 1..5)
function Etoiles({ value, onChange, size = 16 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={onChange ? () => onChange(n === value ? 0 : n) : undefined}
          className={onChange ? 'cursor-pointer' : 'cursor-default'} title={`${n}/5`}>
          <Star size={size} className={n <= (value || 0) ? 'text-gold-500 fill-gold-400' : 'text-gray-300'} />
        </button>
      ))}
    </div>
  );
}

// ───────────────────────── Modale : répondre au candidat ─────────────────────────
function ReponseModal({ recrutement, onClose, onSent }) {
  const [data, setData] = useState(null);
  const [selCle, setSelCle] = useState('');
  const [destinataire, setDestinataire] = useState('');
  const [sujet, setSujet] = useState('');
  const [corps, setCorps] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');

  useEffect(() => {
    recrutementsApi.getModelesReponse(recrutement.id)
      .then(({ data }) => { setData(data); setDestinataire(data.destinataire || ''); })
      .catch(() => setData({ modeles: [], smtp_pret: false }));
  }, [recrutement.id]);

  const choisir = (cle) => {
    setSelCle(cle);
    const m = (data?.modeles || []).find(x => x.cle === cle);
    if (m) { setSujet(m.sujet); setCorps(m.corps); }
  };
  const envoyer = async () => {
    if (!corps.trim() || !destinataire.trim()) return;
    setBusy(true); setErr('');
    try {
      const nom = (data?.modeles || []).find(x => x.cle === selCle)?.nom;
      await recrutementsApi.repondre(recrutement.id, { destinataire, sujet, corps, modele_nom: nom });
      setDone('Réponse envoyée.'); onSent && onSent(); setTimeout(onClose, 1500);
    } catch (e) { setErr(e.response?.data?.error || "Échec de l'envoi."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-8 p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg text-navy-900 flex items-center gap-2"><Send size={17} className="text-navy-500" /> Répondre à {nomComplet(recrutement)}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>
        {done ? (
          <div className="py-8 text-center text-emerald-600 text-sm">{done}</div>
        ) : (
          <div className="space-y-3">
            {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg px-3 py-2">{err}</div>}
            {data && data.smtp_pret === false && <div className="bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-lg px-3 py-2">L'envoi d'e-mails n'est pas encore activé — vous pouvez copier le message.</div>}
            <label className="text-sm block">Modèle de réponse
              <select className="form-select mt-1" value={selCle} onChange={e => choisir(e.target.value)}>
                <option value="">— Choisir un modèle —</option>
                {(data?.modeles || []).map(m => <option key={m.cle} value={m.cle}>{m.nom}</option>)}
              </select>
            </label>
            <label className="text-sm block">Destinataire
              <input type="email" className="form-input mt-1" value={destinataire} onChange={e => setDestinataire(e.target.value)} placeholder="email@candidat.fr" />
            </label>
            <label className="text-sm block">Objet
              <input className="form-input mt-1" value={sujet} onChange={e => setSujet(e.target.value)} />
            </label>
            <label className="text-sm block">Message
              <textarea rows={8} className="form-input mt-1 leading-relaxed" value={corps} onChange={e => setCorps(e.target.value)} />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => navigator.clipboard?.writeText(corps)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600">Copier</button>
              <button onClick={envoyer} disabled={busy || !corps.trim() || !destinataire.trim()} className="btn-primary flex items-center gap-1.5 text-sm"><Send size={14} /> {busy ? 'Envoi…' : 'Envoyer'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ───────────────────────── Formulaire (création / édition) ─────────────────────────
function RecrutementForm({ initial, structures, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    poste: '', nom: '', prenom: '', email: '', telephone: '', source: '', structure_id: '',
    statut: 'a_traiter', type_demande: 'poste', note_globale: 0, disponibilite: '', pretentions: '', commentaire: '',
    date_candidature: new Date().toISOString().slice(0, 10), ...(initial || {}),
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!f.nom?.trim() && !f.prenom?.trim()) { setErr('Indiquez au moins le nom ou le prénom.'); return; }
    setSaving(true);
    try {
      if (initial?.id) await recrutementsApi.update(initial.id, f);
      else await recrutementsApi.create(f);
      onSaved();
    } catch (e2) { setErr(e2.response?.data?.error || 'Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-2xl my-8 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg text-navy-900">{initial?.id ? 'Modifier la candidature' : 'Nouvelle candidature'}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
        </div>
        {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg px-3 py-2">{err}</div>}
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm">Prénom<input className="form-input mt-1" value={f.prenom || ''} onChange={e => set('prenom', e.target.value)} /></label>
          <label className="text-sm">Nom<input className="form-input mt-1" value={f.nom || ''} onChange={e => set('nom', e.target.value)} /></label>
          <label className="text-sm">Type de demande
            <select className="form-select mt-1" value={f.type_demande || 'poste'} onChange={e => set('type_demande', e.target.value)}>
              {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>
          <label className="text-sm">Poste visé<input className="form-input mt-1" placeholder="Auxiliaire petite enfance…" value={f.poste || ''} onChange={e => set('poste', e.target.value)} /></label>
          <label className="text-sm">Structure
            <select className="form-select mt-1" value={f.structure_id || ''} onChange={e => set('structure_id', e.target.value)}>
              <option value="">— Indifférent —</option>
              {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          </label>
          <label className="text-sm">E-mail<input type="email" className="form-input mt-1" value={f.email || ''} onChange={e => set('email', e.target.value)} /></label>
          <label className="text-sm">Téléphone<input className="form-input mt-1" value={f.telephone || ''} onChange={e => set('telephone', e.target.value)} /></label>
          <label className="text-sm">Source
            <select className="form-select mt-1" value={f.source || ''} onChange={e => set('source', e.target.value)}>
              <option value="">—</option>
              {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="text-sm">Date de candidature<input type="date" className="form-input mt-1" value={(f.date_candidature || '').slice(0, 10)} onChange={e => set('date_candidature', e.target.value)} /></label>
          <label className="text-sm">Disponibilité<input className="form-input mt-1" placeholder="Immédiate, 01/09…" value={f.disponibilite || ''} onChange={e => set('disponibilite', e.target.value)} /></label>
          <label className="text-sm">Prétentions salariales<input className="form-input mt-1" value={f.pretentions || ''} onChange={e => set('pretentions', e.target.value)} /></label>
          <label className="text-sm">Statut
            <select className="form-select mt-1" value={f.statut} onChange={e => set('statut', e.target.value)}>
              {STATUTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
          <label className="text-sm">Appréciation
            <div className="mt-2"><Etoiles value={f.note_globale} onChange={(v) => set('note_globale', v)} size={20} /></div>
          </label>
        </div>
        <label className="text-sm block">Commentaire de synthèse
          <textarea className="form-input mt-1" rows={3} value={f.commentaire || ''} onChange={e => set('commentaire', e.target.value)} />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </form>
    </div>
  );
}

// ───────────────────────── Fiche détail (CV + notes) ─────────────────────────
function RecrutementDetail({ id, structures, onBack, onChanged }) {
  const [r, setR] = useState(null);
  const [note, setNote] = useState('');
  const [typeNote, setTypeNote] = useState('note');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [repondre, setRepondre] = useState(false);

  const load = useCallback(() => { recrutementsApi.getById(id).then(({ data }) => setR(data)).catch(() => setR(null)); }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!r) return <div className="py-10 text-center text-gray-400">Chargement…</div>;

  const changeStatut = async (statut) => { await recrutementsApi.setStatut(r.id, statut); load(); onChanged?.(); };
  const embaucher = async () => {
    if (!window.confirm(`Embaucher ${nomComplet(r)} ? Une fiche salarié va être créée et le dossier d'embauche s'ouvre.`)) return;
    try { await recrutementsApi.embaucher(r.id); load(); onChanged?.(); }
    catch (e) { window.alert(e.response?.data?.error || 'Embauche impossible.'); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try { await recrutementsApi.addNote(r.id, { contenu: note, type: typeNote }); setNote(''); load(); onChanged?.(); }
    finally { setSaving(false); }
  };
  const delNote = async (nid) => { if (!window.confirm('Supprimer cette note ?')) return; await recrutementsApi.deleteNote(nid); load(); onChanged?.(); };

  return (
    <div className="space-y-4">
      {editing && (
        <RecrutementForm initial={r} structures={structures}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); onChanged?.(); }} />
      )}
      {repondre && (
        <ReponseModal recrutement={r} onClose={() => setRepondre(false)} onSent={() => { load(); onChanged?.(); }} />
      )}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-navy-600 hover:text-navy-900"><ArrowLeft size={16} /> Retour à la liste</button>
        <div className="flex items-center gap-2">
          {!r.employe_id && (
            <>
              <button onClick={embaucher} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"><UserCheck size={14} /> Embaucher</button>
              {r.statut !== 'refuse' && <button onClick={() => changeStatut('refuse')} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-rose-50 hover:text-rose-600">Non retenu</button>}
            </>
          )}
          <button onClick={() => setRepondre(true)} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-navy-800 text-white hover:bg-navy-900"><Send size={14} /> Répondre</button>
          <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"><Pencil size={14} /> Modifier</button>
        </div>
      </div>

      {/* En-tête candidat */}
      <div className="apple-card p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-serif text-xl text-navy-900">{nomComplet(r)}</h2>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${typeMeta(r.type_demande).badge}`}>{typeMeta(r.type_demande).label}</span>
              <Etoiles value={r.note_globale} />
            </div>
            <div className="text-sm text-navy-500 mt-1 flex items-center flex-wrap gap-x-4 gap-y-1">
              {r.poste && <span className="flex items-center gap-1"><Briefcase size={13} /> {r.poste}</span>}
              {r.structure_nom && <span>· {r.structure_nom}</span>}
              {r.email && <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-navy-800"><Mail size={13} /> {r.email}</a>}
              {r.telephone && <span className="flex items-center gap-1"><Phone size={13} /> {r.telephone}</span>}
            </div>
            <div className="text-xs text-navy-400 mt-1.5 flex items-center flex-wrap gap-x-3">
              {r.source && <span>Source : {r.source}</span>}
              {r.date_candidature && <span className="flex items-center gap-1"><Calendar size={11} /> candidature du {fmtDate(r.date_candidature)}</span>}
              {r.disponibilite && <span>· dispo : {r.disponibilite}</span>}
              {r.pretentions && <span>· prétentions : {r.pretentions}</span>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <span className="text-[11px] text-navy-400">Statut</span>
            <select value={r.statut} onChange={e => changeStatut(e.target.value)} className="form-select text-sm">
              {STATUTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>
        {r.commentaire && <p className="mt-3 text-sm text-navy-700 bg-cream-100 rounded-lg px-3 py-2 whitespace-pre-wrap">{r.commentaire}</p>}
      </div>

      {/* Dossier d'embauche (si le candidat a été embauché) */}
      {r.employe_id && (
        <div className="apple-card p-4 border border-emerald-200 bg-emerald-50/30">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-navy-800 text-sm flex items-center gap-2"><FileCheck size={16} className="text-emerald-600" /> Dossier d'embauche</h3>
            <Link to={`/rh?tab=employes`} className="text-xs text-navy-600 hover:text-navy-900 underline">Ouvrir la fiche salarié</Link>
          </div>
          <div className="text-[12.5px] text-navy-600 mb-3">
            Documents à demander et à charger :
            <ul className="mt-1.5 grid sm:grid-cols-2 gap-x-4 gap-y-1">
              {DOCS_EMBAUCHE.map(d => <li key={d} className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {d}</li>)}
            </ul>
          </div>
          <PiecesJointes entiteType="employe" entiteId={r.employe_id} title="Pièces du dossier (diplôme, honorabilité, CNI, carte vitale, RIB…)" />
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {/* CV & documents */}
        <div className="apple-card p-4">
          <PiecesJointes entiteType="recrutement" entiteId={r.id} title="CV & documents" />
        </div>

        {/* Prise de notes */}
        <div className="apple-card p-4">
          <div className="flex items-center gap-2 mb-3"><StickyNote size={16} className="text-navy-500" /><h3 className="font-semibold text-navy-800 text-sm">Notes & entretiens</h3></div>
          <div className="space-y-2">
            <div className="flex gap-2">
              <select value={typeNote} onChange={e => setTypeNote(e.target.value)} className="form-select text-sm w-32">
                <option value="note">Note</option>
                <option value="entretien">Entretien</option>
              </select>
            </div>
            <textarea className="form-input" rows={3} placeholder="Votre note (impressions, points abordés en entretien, suite à donner…)" value={note} onChange={e => setNote(e.target.value)} />
            <div className="flex justify-end">
              <button onClick={addNote} disabled={saving || !note.trim()} className="btn-primary flex items-center gap-1.5 text-sm"><Send size={14} /> Ajouter la note</button>
            </div>
          </div>
          <div className="mt-4 space-y-2.5">
            {(r.notes || []).length === 0 && <div className="text-sm text-gray-400 text-center py-3">Aucune note pour l'instant.</div>}
            {(r.notes || []).map(n => (
              <div key={n.id} className="rounded-lg border border-cream-300 p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${n.type === 'entretien' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{n.type === 'entretien' ? 'Entretien' : 'Note'}</span>
                  <button onClick={() => delNote(n.id)} className="text-gray-300 hover:text-rose-500"><Trash2 size={13} /></button>
                </div>
                <p className="text-sm text-navy-800 whitespace-pre-wrap">{n.contenu}</p>
                <div className="text-[11px] text-navy-400 mt-1.5">{n.auteur_nom || 'Anonyme'} · {fmtDateHeure(n.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────── Page principale (liste) ─────────────────────────
export default function RecrutementPage({ structures = [] }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selId, setSelId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [fStruct, setFStruct] = useState('');
  const [fStatut, setFStatut] = useState('');
  const [fType, setFType] = useState('');
  const [relevant, setRelevant] = useState(false);
  const [relMsg, setRelMsg] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (fStruct) params.structure_id = fStruct;
    if (fStatut) params.statut = fStatut;
    if (fType) params.type_demande = fType;
    recrutementsApi.getAll(params).then(({ data }) => setList(Array.isArray(data) ? data : [])).catch(() => setList([])).finally(() => setLoading(false));
  }, [fStruct, fStatut, fType]);
  useEffect(() => { load(); }, [load]);

  const del = async (r, e) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer définitivement la candidature de ${nomComplet(r)} (CV et notes compris) ?`)) return;
    await recrutementsApi.delete(r.id); load();
  };

  const relever = async () => {
    setRelevant(true); setRelMsg(null);
    try {
      const { data } = await recrutementsApi.relever();
      setRelMsg({ t: 'ok', m: data.message });
      load();
    } catch (e) {
      setRelMsg({ t: 'err', m: e.response?.data?.error || 'Relevé impossible.' });
    }
    setRelevant(false);
  };

  if (selId) {
    return <RecrutementDetail id={selId} structures={structures} onBack={() => { setSelId(null); load(); }} onChanged={load} />;
  }

  const compteur = STATUTS.map(s => ({ ...s, n: list.filter(r => r.statut === s.key).length }));

  return (
    <div className="space-y-4">
      {showForm && (
        <RecrutementForm structures={structures} onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }} />
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <select value={fStruct} onChange={e => setFStruct(e.target.value)} className="form-select text-sm w-52">
            <option value="">Toutes les structures</option>
            {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
          <select value={fType} onChange={e => setFType(e.target.value)} className="form-select text-sm w-40">
            <option value="">Tous les types</option>
            {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <select value={fStatut} onChange={e => setFStatut(e.target.value)} className="form-select text-sm w-40">
            <option value="">Tous les statuts</option>
            {STATUTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={relever} disabled={relevant} className="btn-secondary flex items-center gap-1.5" title="Importer les candidatures reçues sur recrutement@">
            {relevant ? <Loader size={15} className="animate-spin" /> : <RefreshCw size={15} />} Relever la boîte
          </button>
          <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-1.5"><UserPlus size={16} /> Nouvelle candidature</button>
        </div>
      </div>
      {relMsg && <div className={`text-[13px] rounded-lg px-3 py-2 ${relMsg.t === 'err' ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`}>{relMsg.m}</div>}

      {/* Compteurs par statut */}
      <div className="flex flex-wrap gap-2">
        {compteur.map(s => (
          <span key={s.key} className={`text-xs px-2.5 py-1 rounded-full ${s.badge}`}>{s.label} : <strong>{s.n}</strong></span>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center text-gray-400">Chargement…</div>
      ) : list.length === 0 ? (
        <div className="apple-card p-10 text-center text-navy-400">
          Aucune candidature pour l'instant.<br />
          <span className="text-sm text-navy-300">Cliquez sur « Nouvelle candidature » pour en ajouter une et charger un CV.</span>
        </div>
      ) : (
        <div className="apple-card overflow-hidden divide-y divide-cream-200">
          {list.map(r => {
            const st = statutMeta(r.statut);
            return (
              <div key={r.id} onClick={() => setSelId(r.id)} className="px-4 py-3 flex items-center gap-3 hover:bg-cream-50 cursor-pointer">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-navy-900">{nomComplet(r)}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${typeMeta(r.type_demande).badge}`}>{typeMeta(r.type_demande).label}</span>
                    {r.note_globale ? <Etoiles value={r.note_globale} size={12} /> : null}
                  </div>
                  <div className="text-xs text-navy-500 truncate">
                    {r.poste || 'Poste non précisé'}{r.structure_nom ? ` · ${r.structure_nom}` : ''}{r.source ? ` · ${r.source}` : ''}{r.date_candidature ? ` · ${fmtDate(r.date_candidature)}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-navy-400">
                  {r.nb_cv > 0 && <span title="CV joints">📎 {r.nb_cv}</span>}
                  {r.nb_notes > 0 && <span title="Notes">📝 {r.nb_notes}</span>}
                  <span className={`px-2 py-0.5 rounded-full font-medium ${st.badge}`}>{st.label}</span>
                  <button onClick={(e) => del(r, e)} className="text-gray-300 hover:text-rose-500" title="Supprimer"><Trash2 size={15} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
